import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  Card,
  CardContent,
  ButtonGroup,
  Checkbox
} from '@mui/material';
import ActionButtons, { ActionButton } from './ActionButtons';
import { 
  Visibility as VisibilityIcon, 
  Delete as DeleteIcon, 
  Chat as ChatIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Fingerprint as FingerprintIcon,
  Save as SaveIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Refresh as RefreshIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { conversationsService, Conversation as ServiceConversation, Message as ServiceMessage } from '../services/conversationsService';
import { ratingsService, Rating as ServiceRating } from '../services/ratingsService';
// Note: Using API endpoints instead of direct Firestore access for better security
import { adminConfig } from '../config/firebase-config.js';

interface Conversation {
  id: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  messageCount: number;
  status: "active" | "ended";
  conversationType?: 'chat' | 'trip_status' | 'check_availability'; // Add conversation type
  initialButtonType?: string; // Add initial button type
  sessionInfo?: {
    userAgent: string;
    deviceType: string;
    referrer: string;
    ipHash: string;
  };
  lastMessage?: string;
  firstMessage?: string;
  messagePreview?: {
    userMessage: string;
    aiResponse: string;
    timestamp: Date;
  };
  initialStatusMessage?: string;
}



interface Rating {
  id: string;
  conversationId: string;
  messageId?: string;
  rating: 'good' | 'bad' | 'wrong';
  notes?: string;
  adminEmail: string;
  timestamp: Date;
  resolved?: boolean;
  context?: {
    userMessage: string;
    aiResponse: string;
  };
}

interface RatingFormData {
  conversationId: string;
  messageId?: string;
  rating: 'good' | 'bad' | 'wrong';
  notes: string;
}

// Admin API helper functions (same pattern as AIDataPage)
const getApiBaseUrl = () => {
  return adminConfig.apiBaseUrl;
};

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': 'geoff@emertech.nz'
});

const ConversationsPage: React.FC = () => {
  const [conversations, setConversations] = useState<ServiceConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasMore, setHasMore] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<ServiceConversation | null>(null);
  const [messages, setMessages] = useState<ServiceMessage[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Rating states
  const [ratings, setRatings] = useState<ServiceRating[]>([]);
  const [ratingFormData, setRatingFormData] = useState<RatingFormData>({
    conversationId: '',
    messageId: undefined,
    rating: 'good',
    notes: ''
  });
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [ratingTarget, setRatingTarget] = useState<'conversation' | 'message'>('conversation');
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [editingRating, setEditingRating] = useState<Rating | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [ratingSaving, setRatingSaving] = useState(false);
  
  // Multi-select states
  const [selectedConversations, setSelectedConversations] = useState<Set<string>>(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { conversations: convs, hasMore: hasMoreResults } = await conversationsService.getAllConversations(
        rowsPerPage,
        page * rowsPerPage
      );
      
      // Only show conversations that included a real user chat submission.
      // Older records may not have `hasChatSubmission`, so use `messageCount >= 2` as a safe fallback
      // (a normal chat exchange is at least user + assistant).
      const conversationsList: ServiceConversation[] = convs.filter((c) =>
        c.hasChatSubmission === true || (c.messageCount ?? 0) >= 2
      );
      
      // Debug logging
      console.log(`📊 ConversationsPage: Loaded ${conversationsList.length} conversations`);
      const typeBreakdown = {
        trip_status: conversationsList.filter(c => c.conversationType === 'trip_status').length,
        check_availability: conversationsList.filter(c => c.conversationType === 'check_availability').length,
        chat: conversationsList.filter(c => !c.conversationType || c.conversationType === 'chat').length,
        unknown: conversationsList.filter(c => c.conversationType && !['trip_status', 'check_availability', 'chat'].includes(c.conversationType)).length
      };
      console.log('📊 Conversation types breakdown:', typeBreakdown);
      console.log('📋 First 5 conversations:', conversationsList.slice(0, 5).map(c => ({
        id: c.id.substring(0, 8),
        type: c.conversationType || 'chat',
        messageCount: c.messageCount,
        hasPreview: !!c.messagePreview
      })));
      
      setConversations(conversationsList);
      setHasMore(hasMoreResults);
      setLastUpdated(new Date());
      
      if (conversationsList.length === 0) {
        console.log('No conversations found.');
      }
    } catch (error: any) {
      console.error('Error loading conversations:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage, page]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const loadMessages = async (conversationId: string) => {
    try {
      setMessagesLoading(true);
      
      // Load messages and ratings in parallel using service account APIs
      const [messages, conversationRatings] = await Promise.all([
        conversationsService.getMessages(conversationId),
        ratingsService.getRatingsForConversation(conversationId)
      ]);

      setMessages(messages);
      setRatings(conversationRatings);
      console.log(`Loaded ${messages.length} messages and ${conversationRatings.length} ratings for conversation ${conversationId}`);
      
    } catch (error: any) {
      console.error('Error loading messages:', error);
      setError(error.message);
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadRatings = async (conversationId: string) => {
    try {
      // Use Firebase Functions API (works in both dev and production)
      const response = await fetch(`${getApiBaseUrl()}/getRatings?conversationId=${conversationId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        const ratingsData = data.ratings || [];
        console.log('Ratings loaded via Functions API');
        
        setRatings(ratingsData.map((rating: any) => ({
          ...rating,
          timestamp: rating.timestamp instanceof Date ? rating.timestamp : new Date(rating.timestamp)
        })));
      } else {
        console.error('Failed to load ratings via API');
        setRatings([]);
      }
    } catch (error: any) {
      console.error('Error loading ratings:', error);
      setRatings([]);
    }
  };

  const handleCreateRating = async () => {
    setRatingSaving(true);
    try {
      const targetMessage = selectedMessageId ? messages.find(m => m.id === selectedMessageId) : null;
      const userMessage = targetMessage?.role === 'user' ? targetMessage : 
                         messages.find(m => m.role === 'user');
      const aiMessage = targetMessage?.role === 'assistant' ? targetMessage :
                       messages.find(m => m.role === 'assistant');

      const ratingData = {
        conversationId: ratingFormData.conversationId,
        messageId: ratingTarget === 'message' ? selectedMessageId : undefined,
        rating: ratingFormData.rating,
        notes: ratingFormData.notes,
        context: {
          userMessage: userMessage?.content || '',
          aiResponse: aiMessage?.content || ''
        }
      };

      // Use Firebase Functions API (works in both dev and production)
      const response = await fetch(`${getApiBaseUrl()}/createRating`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(ratingData),
      });

      if (response.ok) {
        console.log('Rating saved via Functions API');
        
        // Reload ratings
        await loadRatings(ratingFormData.conversationId);
        
        // Reset form
        setShowRatingForm(false);
        setRatingFormData({
          conversationId: '',
          messageId: undefined,
          rating: 'good',
          notes: ''
        });
        setSelectedMessageId(null);
        
        // Clear any previous errors
        setError(null);
        console.log('Rating created successfully!');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create rating');
      }
      
    } catch (error: any) {
      console.error('Error creating rating:', error);
      setError(`Failed to create rating: ${error.message}`);
    } finally {
      setRatingSaving(false);
    }
  };

  const handleUpdateRating = async () => {
    setRatingSaving(true);
    try {
      if (!editingRating) return;

      const targetMessage = selectedMessageId ? messages.find(m => m.id === selectedMessageId) : null;
      const userMessage = targetMessage?.role === 'user' ? targetMessage : 
                         messages.find(m => m.role === 'user');
      const aiMessage = targetMessage?.role === 'assistant' ? targetMessage :
                       messages.find(m => m.role === 'assistant');

      const ratingData = {
        conversationId: ratingFormData.conversationId,
        messageId: ratingTarget === 'message' ? selectedMessageId : undefined,
        rating: ratingFormData.rating,
        notes: ratingFormData.notes,
        context: {
          userMessage: userMessage?.content || '',
          aiResponse: aiMessage?.content || ''
        }
      };

      // Use Firebase Functions API (works in both dev and production)
      const response = await fetch(`${getApiBaseUrl()}/updateRating`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ratingId: editingRating.id,
          ...ratingData
        }),
      });

      if (response.ok) {
        console.log('Rating updated via Functions API');
        
        // Reload ratings
        await loadRatings(ratingFormData.conversationId);
        
        // Reset form
        setShowRatingForm(false);
        setIsEditMode(false);
        setEditingRating(null);
        setRatingFormData({
          conversationId: '',
          messageId: undefined,
          rating: 'good',
          notes: ''
        });
        setSelectedMessageId(null);
        
        // Clear any previous errors
        setError(null);
        console.log('Rating updated successfully!');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update rating');
      }
      
    } catch (error: any) {
      console.error('Error updating rating:', error);
      setError(`Failed to update rating: ${error.message}`);
    } finally {
      setRatingSaving(false);
    }
  };

  const handleEditRating = (rating: Rating) => {
    setEditingRating(rating);
    setIsEditMode(true);
    setRatingFormData({
      conversationId: rating.conversationId,
      messageId: rating.messageId,
      rating: rating.rating,
      notes: rating.notes || ''
    });
    setRatingTarget(rating.messageId ? 'message' : 'conversation');
    setSelectedMessageId(rating.messageId || null);
    setShowRatingForm(true);
  };

  const handleViewConversation = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setDialogOpen(true);
    await loadMessages(conversation.id);
  };

  const handleRowClick = (conversation: Conversation, event: React.MouseEvent) => {
    // Don't trigger if clicking on action buttons
    if ((event.target as HTMLElement).closest('.action-buttons')) {
      return;
    }
    handleViewConversation(conversation);
  };

  const handleDeleteConversation = async (conversationId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click

    if (!conversationId) {
      console.error('Invalid conversation ID provided for deletion');
      setError('Invalid conversation ID');
      return;
    }

    if (window.confirm('Are you sure you want to delete this conversation? This will also delete all associated messages and ratings.')) {
      try {
        console.log(`Starting deletion of conversation ${conversationId} via API`);

        // Use Firebase Functions API (works in both dev and production)
        const response = await fetch(`${getApiBaseUrl()}${adminConfig.endpoints.deleteConversation}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({ conversationId }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Conversation deleted successfully via API:', result);

          // Clear any previous errors and refresh the list
          setError(null);
          await loadConversations();
        } else {
          const errorData = await response.json();
          console.error('Failed to delete conversation via API:', errorData);
          throw new Error(errorData.error || 'Failed to delete conversation');
        }

      } catch (error: any) {
        console.error('Error deleting conversation:', error);

        // More specific error messages
        if (error.message?.includes('not found')) {
          setError('Conversation not found. It may have already been deleted.');
        } else if (error.message?.includes('permission')) {
          setError('Permission denied. You may not have access to delete this conversation.');
        } else {
          setError(`Failed to delete conversation: ${error.message || 'Unknown error'}`);
        }

        // Still refresh the list in case of partial deletion
        await loadConversations();
      }
    }
  };

  const handleStartRating = (target: 'conversation' | 'message', messageId?: string) => {
    setRatingTarget(target);
    setSelectedMessageId(messageId || null);
    setRatingFormData({
      conversationId: selectedConversation?.id || '',
      messageId: target === 'message' ? messageId : undefined,
      rating: 'good',
      notes: ''
    });
    setShowRatingForm(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedConversation(null);
    setMessages([]);
    setRatings([]);
    setShowRatingForm(false);
    setSelectedMessageId(null);
    setIsEditMode(false);
    setEditingRating(null);
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getRatingIcon = (rating: 'good' | 'bad' | 'wrong') => {
    switch (rating) {
      case 'good': return <ThumbUpIcon />;
      case 'bad': return <ThumbDownIcon />;
      case 'wrong': return <WarningIcon />;
      default: return <StarBorderIcon />;
    }
  };

  const getRatingColor = (rating: 'good' | 'bad' | 'wrong') => {
    switch (rating) {
      case 'good': return 'success';
      case 'bad': return 'warning';
      case 'wrong': return 'error';
      default: return 'default';
    }
  };

  const getMessageRating = (messageId: string) => {
    return ratings.find(r => r.messageId === messageId);
  };

  const getConversationRating = () => {
    return ratings.find(r => !r.messageId);
  };

  // Multi-select handlers
  const handleSelectConversation = (conversationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newSelected = new Set(selectedConversations);
    if (newSelected.has(conversationId)) {
      newSelected.delete(conversationId);
    } else {
      newSelected.add(conversationId);
    }
    setSelectedConversations(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedConversations.size === conversations.length) {
      setSelectedConversations(new Set());
    } else {
      setSelectedConversations(new Set(conversations.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedConversations.size === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedConversations.size} conversation(s)? This will also delete all associated messages and ratings. This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      let successfulDeletions = 0;
      let failedDeletions = 0;

      console.log(`Starting bulk deletion of ${selectedConversations.size} conversations via API`);

      // Process each conversation individually for better error handling
      for (const conversationId of selectedConversations) {
        try {
          console.log(`Processing conversation ${conversationId} via API`);

          // Use Firebase Functions API (works in both dev and production)
          const response = await fetch(`${getApiBaseUrl()}${adminConfig.endpoints.deleteConversation}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            body: JSON.stringify({ conversationId }),
          });

          if (response.ok) {
            const result = await response.json();
            console.log(`Successfully deleted conversation ${conversationId} via API:`, result);
            successfulDeletions++;
          } else {
            const errorData = await response.json();
            console.error(`Failed to delete conversation ${conversationId} via API:`, errorData);
            throw new Error(errorData.error || 'Failed to delete conversation');
          }

        } catch (conversationError: any) {
          console.error(`Error deleting conversation ${conversationId}:`, conversationError);
          failedDeletions++;
        }
      }

      console.log(`Bulk deletion completed: ${successfulDeletions} successful, ${failedDeletions} failed`);

      // Show appropriate success/error message
      if (successfulDeletions > 0) {
        if (failedDeletions > 0) {
          setError(`${successfulDeletions} conversation(s) deleted successfully, but ${failedDeletions} failed.`);
        } else {
          setError(null); // Clear any previous errors
        }
      } else if (failedDeletions > 0) {
        setError(`Failed to delete any conversations. Check console for details.`);
      }

      setSelectedConversations(new Set());
      setIsMultiSelectMode(false);
      await loadConversations();

    } catch (error: any) {
      console.error('Error in bulk deletion process:', error);
      setError(`Bulk deletion failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    setSelectedConversations(new Set());
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading conversations: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1">
          Conversations
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          {isMultiSelectMode && selectedConversations.size > 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="primary">
                {selectedConversations.size} selected
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={loading}
              >
                Delete Selected
              </Button>
            </Box>
          )}
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Last updated: {format(lastUpdated, 'MMM dd, yyyy HH:mm:ss')}
            </Typography>
          )}
          <Button
            variant={isMultiSelectMode ? "contained" : "outlined"}
            startIcon={isMultiSelectMode ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
            onClick={toggleMultiSelectMode}
            disabled={loading}
          >
            {isMultiSelectMode ? "Exit Select" : "Select"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadConversations}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', height: 'calc(100vh - 280px)' }}>
        <TableContainer sx={{ height: 'calc(100% - 52px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {isMultiSelectMode && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedConversations.size > 0 && selectedConversations.size < conversations.length}
                      checked={conversations.length > 0 && selectedConversations.size === conversations.length}
                      onChange={handleSelectAll}
                      inputProps={{ 'aria-label': 'select all conversations' }}
                    />
                  </TableCell>
                )}
                <TableCell width="140px">Created</TableCell>
                <TableCell width="80px" align="center">Messages</TableCell>
                <TableCell width="400px">Message Preview</TableCell>
                <TableCell width="100px">Device</TableCell>
                <TableCell width="120px" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.map((conversation) => (
                <TableRow 
                  hover 
                  key={conversation.id}
                  onClick={(event) => {
                    if (isMultiSelectMode) {
                      handleSelectConversation(conversation.id, event);
                    } else {
                      handleRowClick(conversation, event);
                    }
                  }}
                  sx={{ 
                    cursor: 'pointer',
                    backgroundColor: selectedConversations.has(conversation.id) ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                    '&:hover': {
                      backgroundColor: selectedConversations.has(conversation.id) 
                        ? 'rgba(25, 118, 210, 0.12)' 
                        : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  {isMultiSelectMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedConversations.has(conversation.id)}
                        onChange={(event) => {
                          event.stopPropagation();
                          const newSelected = new Set(selectedConversations);
                          if (newSelected.has(conversation.id)) {
                            newSelected.delete(conversation.id);
                          } else {
                            newSelected.add(conversation.id);
                          }
                          setSelectedConversations(newSelected);
                        }}
                        inputProps={{ 'aria-labelledby': `conversation-${conversation.id}` }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">
                      {format(conversation.createdAt, 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(conversation.createdAt, 'HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" gap={0.5} alignItems="center">
                    <Chip
                      icon={<ChatIcon />}
                      label={conversation.messageCount}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                      {conversation.conversationType && conversation.conversationType !== 'chat' && (
                        <Chip
                          label={conversation.conversationType === 'trip_status' ? 'Trip Status' : 'Check Availability'}
                          color={conversation.conversationType === 'trip_status' ? 'secondary' : 'success'}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {conversation.messagePreview ? (
                      <Box>
                        <Box sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">
                            User:
                          </Typography>
                          <Typography variant="body2" sx={{ pl: 1 }}>
                            {truncateText(conversation.messagePreview.userMessage, 80)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="primary" fontWeight="bold">
                            AI:
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                            {truncateText(conversation.messagePreview.aiResponse, 100)}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                      <Typography variant="body2" color="text.disabled" fontStyle="italic">
                        No messages yet
                        </Typography>
                        {conversation.conversationType && conversation.conversationType !== 'chat' && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            (Button click only)
                      </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {conversation.sessionInfo?.deviceType || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" className="action-buttons">
                    <Tooltip title="View conversation details">
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleViewConversation(conversation);
                        }}
                        size="small"
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete conversation">
                      <IconButton
                        onClick={(event) => handleDeleteConversation(conversation.id, event)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={hasMore ? (page + 1) * rowsPerPage + 1 : page * rowsPerPage + conversations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ChatIcon color="primary" />
              <Typography variant="h6">Conversation Details</Typography>
            </Box>
            
            {selectedConversation && (
              <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <FingerprintIcon fontSize="small" color="action" />
                  <Chip 
                    label={selectedConversation.id.substring(0, 12)}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon fontSize="small" color="action" />
                  <Chip 
                    label={selectedConversation.userId.substring(0, 16)}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
          
          {selectedConversation && (
            <Box mt={2}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" color="text.secondary">
                  Created: {format(selectedConversation.createdAt, 'MMM dd, yyyy HH:mm')} • 
                  Messages: {selectedConversation.messageCount} • 
                  Device: {selectedConversation.sessionInfo?.deviceType || 'Unknown'}
                  {selectedConversation.conversationType && selectedConversation.conversationType !== 'chat' && (
                    <> • Type: <strong>{selectedConversation.conversationType.replace('_', ' ')}</strong></>
                  )}
                </Typography>
                
                <Box display="flex" alignItems="center" gap={1}>
                  {getConversationRating() ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        icon={getRatingIcon(getConversationRating()!.rating)}
                        label={`Conversation: ${getConversationRating()!.rating}`}
                        color={getRatingColor(getConversationRating()!.rating) as any}
                        size="small"
                      />
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleEditRating(getConversationRating()!)}
                        sx={{ fontSize: '0.75rem', minWidth: 'auto', color: 'text.secondary' }}
                      >
                        Edit Rating
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<StarIcon />}
                      onClick={() => handleStartRating('conversation')}
                    >
                      Rate Conversation
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogTitle>
        
        <DialogContent>
          {messagesLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
              {/* Display initial status message if it exists */}
              {selectedConversation?.initialStatusMessage && (
                <Card
                  sx={{
                    mb: 2,
                    backgroundColor: '#fff8e1',
                    border: '2px solid #ffd54f',
                    position: 'relative'
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Chip
                        label="Initial Trip Status"
                        color="warning"
                        size="small"
                        icon={<ChatIcon />}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Shown via Trip Status button
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {selectedConversation.initialStatusMessage}
                    </Typography>
                  </CardContent>
                </Card>
              )}
              
              {messages.map((message, index) => {
                const messageRating = getMessageRating(message.id);

                return (
                  <Card
                    key={message.id}
                    sx={{
                      mb: 2,
                      backgroundColor: message.role === 'user' ? '#f8f9fa' : '#e3f2fd',
                      border: message.role === 'user' ? '1px solid #e9ecef' : '1px solid #bbdefb',
                      position: 'relative'
                    }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={message.role === 'user' ? 'User' : 'AI Assistant'}
                            color={message.role === 'user' ? 'default' : 'primary'}
                            size="small"
                            icon={message.role === 'user' ? undefined : <ChatIcon />}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {format(message.timestamp, 'MMM dd, yyyy HH:mm:ss')}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                          {messageRating ? (
                            <>
                              <Chip
                                icon={getRatingIcon(messageRating.rating)}
                                label={messageRating.rating}
                                color={getRatingColor(messageRating.rating) as any}
                                size="small"
                              />
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleEditRating(messageRating)}
                                sx={{ 
                                  fontSize: '0.75rem', 
                                  minWidth: 'auto', 
                                  color: 'text.secondary',
                                  borderColor: 'text.secondary',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    color: 'primary.main'
                                  }
                                }}
                              >
                                Edit Rating
                              </Button>
                            </>
                          ) : (
                            message.role === 'assistant' && (
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<StarBorderIcon />}
                                onClick={() => handleStartRating('message', message.id)}
                              >
                                Rate
                              </Button>
                            )
                          )}
                        </Box>
                      </Box>
                      
                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        {message.content}
                      </Typography>

                      {/* RAG Sources (if available) */}
                      {message.metadata?.ragSources && message.metadata.ragSources.length > 0 && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600,
                            display: 'block',
                            mb: 0.5,
                            color: 'primary.main'
                          }}>
                            📚 Sources ({message.metadata.ragSources.length}):
                          </Typography>
                          {message.metadata.ragSources
                            .map((source: any, index: number) => ({ ...source, originalIndex: index }))
                            .sort((a: any, b: any) => (b.similarity || 0) - (a.similarity || 0))
                            .map((source: any, sortedIndex: number) => {
                              const isPrimary = sortedIndex === 0;
                              
                              const getTierLabel = (tier: number) => {
                                switch (tier) {
                                  case 1: return 'Critical';
                                  case 2: return 'Core';
                                  case 3: return 'Supporting';
                                  case 4: return 'Marketing';
                                  default: return 'Unknown';
                                }
                              };
                              
                              const getTierColor = (tier: number) => {
                                switch (tier) {
                                  case 1: return '#d32f2f'; // Red for Critical
                                  case 2: return '#ff9800'; // Orange for Core
                                  case 3: return '#1976d2'; // Blue for Supporting
                                  case 4: return '#388e3c'; // Green for Marketing
                                  default: return '#757575';
                                }
                              };

                              return (
                                <Box key={source.originalIndex} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ 
                                    fontSize: '0.75rem',
                                    display: 'block',
                                    lineHeight: 1.4
                                  }}>
                                    <span style={{ 
                                      color: getTierColor(source.tier || 0),
                                      fontWeight: 600
                                    }}>
                                      {getTierLabel(source.tier || 0)}
                                    </span>
                                    {' • '}
                                    <span style={{ 
                                      fontWeight: isPrimary ? 600 : 500
                                    }}>
                                      {source.title || 'Unknown Source'}
                                    </span>
                                    {isPrimary && (
                                      <span style={{ 
                                        fontWeight: 600,
                                        color: '#2e7d32',
                                        marginLeft: 4
                                      }}>
                                        (primary)
                                      </span>
                                    )}
                                    {source.category && (
                                      <>
                                        {' • '}
                                        <span style={{ color: '#666' }}>
                                          {source.category}
                                        </span>
                                      </>
                                    )}
                                    {' • '}
                                    <span style={{ color: '#666' }}>
                                      {Math.round((source.similarity || 0) * 100)}% match
                                    </span>
                                  </Typography>
                                </Box>
                              );
                            })}
                        </Box>
                      )}

                      {/* Availability Action Indicator (if availability was provided) */}
                      {message.role === 'assistant' && message.metadata?.availabilityAction?.openChecker && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label="Provided Availability Information"
                              color="success"
                              size="small"
                              icon={<CalendarMonthIcon />}
                            />
                            {message.metadata.availabilityAction.date && (
                              <Typography variant="caption" color="text.secondary">
                                Date: {new Date(message.metadata.availabilityAction.date).toLocaleDateString()}
                              </Typography>
                            )}
                            {message.metadata.availabilityAction.filter && message.metadata.availabilityAction.filter !== 'All' && (
                              <Typography variant="caption" color="text.secondary">
                                Filter: {message.metadata.availabilityAction.filter}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Action Buttons (if available) */}
                      {message.actions && message.actions.length > 0 && (
                        <ActionButtons 
                          actions={message.actions.map((action, index) => ({
                            id: `action-${message.id}-${index}`,
                            type: action.type as 'book' | 'call' | 'email' | 'link' | 'quote',
                            label: action.label,
                            url: action.url,
                            style: 'primary' as const
                          }))} 
                          onButtonClick={(action: ActionButton) => {
                            console.log('Action button clicked in admin:', action);
                          }}
                        />
                      )}

                      {/* Rating Notes (if available) */}
                      {getMessageRating(message.id)?.notes && (
                        <Box sx={{ 
                          mt: 1.5, 
                          p: 1.5, 
                          bgcolor: 'grey.50', 
                          borderRadius: 1,
                          borderLeft: '4px solid',
                          borderLeftColor: 'grey.400'
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600,
                            display: 'block',
                            mb: 0.5,
                            color: 'text.primary'
                          }}>
                            📝 Rating Notes ({getMessageRating(message.id)!.rating}):
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }}>
                            {getMessageRating(message.id)!.notes}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                    
                    {index < messages.length - 1 && (
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          bottom: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 8,
                          height: 8,
                          backgroundColor: 'background.paper',
                          borderRadius: '50%',
                          border: '2px solid #e0e0e0'
                        }} 
                      />
                    )}
                  </Card>
                );
              })}

              {/* Conversation-level rating notes */}
              {ratings.filter(r => !r.messageId).map((conversationRating) => (
                <Box key={conversationRating.id} sx={{ mt: 2 }}>
                  <Card sx={{ 
                    bgcolor: 'info.light',
                    borderLeft: '4px solid',
                    borderLeftColor: 'info.main'
                  }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark' }}>
                          💬 Conversation Rating
                        </Typography>
                        <Chip
                          icon={getRatingIcon(conversationRating.rating)}
                          label={conversationRating.rating}
                          color={getRatingColor(conversationRating.rating) as any}
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                          by {conversationRating.adminEmail} • {format(conversationRating.timestamp, 'MMM dd, yyyy HH:mm')}
                        </Typography>
                      </Box>
                      {conversationRating.notes && (
                        <Typography variant="body2" sx={{ 
                          color: 'info.dark',
                          fontStyle: 'italic',
                          mt: 1
                        }}>
                          "{conversationRating.notes}"
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              ))}
              
              {messages.length === 0 && (
                <Box textAlign="center" py={4}>
                  <ChatIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No messages found for this conversation.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {/* Rating Form */}
          {showRatingForm && (
            <Card sx={{ mt: 2, backgroundColor: 'background.default' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {isEditMode ? 'Edit' : 'Rate'} {ratingTarget === 'conversation' ? 'Conversation' : 'Message'}
                </Typography>
                
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <ButtonGroup variant="outlined">
                    <Button
                      onClick={() => setRatingFormData({...ratingFormData, rating: 'good'})}
                      variant={ratingFormData.rating === 'good' ? 'contained' : 'outlined'}
                      color="success"
                      startIcon={<ThumbUpIcon />}
                    >
                      Good
                    </Button>
                    <Button
                      onClick={() => setRatingFormData({...ratingFormData, rating: 'bad'})}
                      variant={ratingFormData.rating === 'bad' ? 'contained' : 'outlined'}
                      color="warning"
                      startIcon={<ThumbDownIcon />}
                    >
                      Bad
                    </Button>
                    <Button
                      onClick={() => setRatingFormData({...ratingFormData, rating: 'wrong'})}
                      variant={ratingFormData.rating === 'wrong' ? 'contained' : 'outlined'}
                      color="error"
                      startIcon={<WarningIcon />}
                    >
                      Wrong
                    </Button>
                  </ButtonGroup>
                  
                  <Box display="flex" gap={1}>
                    <Button
                      variant="contained"
                      startIcon={ratingSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={isEditMode ? handleUpdateRating : handleCreateRating}
                      disabled={ratingSaving}
                    >
                      {ratingSaving ? 'Saving...' : (isEditMode ? 'Update Rating' : 'Save Rating')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowRatingForm(false);
                        setIsEditMode(false);
                        setEditingRating(null);
                        setRatingSaving(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes (Optional)"
                  value={ratingFormData.notes}
                  onChange={(e) => setRatingFormData({...ratingFormData, notes: e.target.value})}
                  placeholder="Add any additional feedback about this rating..."
                />
              </CardContent>
            </Card>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationsPage; 